import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { getHttpClient } from '@vnetwork/margarita-core/http';

const foundationApi = getHttpClient('foundationApi');

// ============================================
// Organization data layer — the @mgmt control plane proxies these to
// datacentral-api-v4 at `/partner/organization/*`. Unlike the rest of the v3
// service, payloads are passed THROUGH unchanged: `{ message, result }` for a
// single object, `{ message, results, pagination }` for a list, and
// `{ error, error_description }` on failure. So we read `result`/`results`,
// not the v3 `{ status, data }` envelope.
// ============================================

const BASE = '/partner/organization';

// ============================================
// Types
// ============================================

export interface OrgMe {
  customer_id: number;
  contact_id: number;
  reseller_id: number;
  /** The current caller's login id ("" if the caller has no login). */
  user_uuid: string;
  is_reseller: boolean;
  is_admin: boolean;
  is_primary: boolean;
}

export interface Contact {
  contact_id: number;
  customer_id: number;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  contact_address: string;
  contact_position: string;
  /** Login id for this contact ("" when the contact has no login). */
  user_uuid: string;
  is_primary: boolean;
  has_login: boolean;
  authentication_method: string;
  created_at: string;
  updated_at: string;
}

export interface CreateContactRequest {
  contact_name: string;
  contact_email: string;
  contact_phone?: string;
  contact_address?: string;
  contact_position?: string;
  provision_login?: boolean;
}

export type UpdateContactRequest = Omit<CreateContactRequest, 'provision_login'>;

export interface ChildCustomer {
  id: number;
  customer_id: number;
  customer_name: string;
  customer_email: string;
  status: string;
  joined_at: string;
}

export interface AddChildRequest {
  customer_id: number;
  status?: string;
}

export interface ChildService {
  service_id: number;
  service_name: string;
  service_status: string;
  price_recurring: number;
  customer_id: number;
  customer_name: string;
  customer_email: string;
  created_at: string;
}

export interface ChildInvoice {
  invoice_id: number;
  invoice_status: string;
  invoice_total: number;
  due_date: string;
  customer_id: number;
  customer_name: string;
  customer_email: string;
  created_at: string;
}

export interface OrgStats {
  customer_count: number;
  active_customers: number;
  total_services: number;
  active_services: number;
  mrr: number;
  total_revenue: number;
  pending_revenue: number;
}

export type AdminRole = 'admin' | 'owner' | string;

export interface OrgAdmin {
  id: number;
  user_uuid: string;
  role: AdminRole;
  contact_id: number;
  contact_name: string;
  contact_email: string;
}

export interface AddAdminRequest {
  user_uuid: string;
  role?: AdminRole;
}

export interface ListParams {
  page?: number;
  pageSize?: number;
}

export interface PaginationMeta {
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  page_next: number;
  page_prev: number;
}

export interface Paginated<T> {
  results: T[];
  pagination: PaginationMeta;
}

// ============================================
// Response envelope helpers (pass-through datacentral shapes)
// ============================================

interface ObjectEnvelope<T> {
  message?: string;
  result: T;
}
interface ArrayEnvelope<T> {
  message?: string;
  results: T[];
  pagination?: PaginationMeta;
}

function emptyPagination(pageSize = 20): PaginationMeta {
  return { total: 0, page: 1, page_size: pageSize, total_pages: 1, page_next: 0, page_prev: 0 };
}

// ============================================
// Error mapping — backend returns `{ error, error_description }` where
// error_description is a kebab-case key. Map known keys to friendly copy.
// ============================================

const ERROR_MESSAGES: Record<string, string> = {
  'cannot-delete-primary-contact': 'The primary contact cannot be deleted. Set another contact as primary first.',
  'contact-already-has-login': 'This contact already has a login.',
  'child-already-assigned': 'That customer is already linked as a customer.',
  'user-not-in-org': 'That user is not a contact in your organization.',
  'cannot-remove-last-admin': 'You cannot remove the last administrator.',
};

/** Friendly message for an org API error, falling back to `fallback`. */
export function orgErrorMessage(error: unknown, fallback: string): string {
  const key = (error as { response?: { data?: { error_description?: string } } })?.response?.data
    ?.error_description;
  if (key && ERROR_MESSAGES[key]) return ERROR_MESSAGES[key];
  return fallback;
}

// ============================================
// Query keys
// ============================================

export const orgKeys = {
  all: ['partner', 'organization'] as const,
  me: ['partner', 'organization', 'me'] as const,
  contacts: (params: ListParams) => ['partner', 'organization', 'contacts', params] as const,
  contactsAll: ['partner', 'organization', 'contacts'] as const,
  contact: (id: number) => ['partner', 'organization', 'contact', id] as const,
  children: (params: ListParams) => ['partner', 'organization', 'children', params] as const,
  childrenAll: ['partner', 'organization', 'children'] as const,
  childServices: (params: ListParams) =>
    ['partner', 'organization', 'children', 'services', params] as const,
  childInvoices: (params: ListParams) =>
    ['partner', 'organization', 'children', 'invoices', params] as const,
  stats: ['partner', 'organization', 'stats'] as const,
  admins: ['partner', 'organization', 'admins'] as const,
};

// ============================================
// Generic fetch helpers
// ============================================

async function fetchObject<T>(path: string): Promise<T | null> {
  const res = await foundationApi.get<ObjectEnvelope<T>>(`${BASE}${path}`);
  return res.data?.result ?? null;
}

async function fetchList<T>(path: string, params: ListParams): Promise<Paginated<T>> {
  // Backend pages by limit/offset; translate from the UI's page/pageSize.
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;
  const res = await foundationApi.get<ArrayEnvelope<T>>(`${BASE}${path}`, {
    params: {
      limit: pageSize,
      offset: Math.max(0, (page - 1) * pageSize),
    },
  });
  return {
    results: res.data?.results ?? [],
    pagination: res.data?.pagination ?? emptyPagination(params.pageSize),
  };
}

// ============================================
// Identity / gating
// ============================================

/**
 * The query behind `useOrgMe`; `load` is the request, a test passes a counting one. `retryOnMount: false`: a failed /me with no data
 * is not asked again by every observer that mounts (that, with a skeleton shown while loading, re-requested without end); it is
 * asked again by a window focus, a reconnect, or the entry being collected.
 */
export function orgMeQuery(load: () => Promise<OrgMe | null> = () => fetchObject<OrgMe>('/me')) {
  return {
    queryKey: orgKeys.me,
    staleTime: 5 * 60_000,
    retryOnMount: false,
    queryFn: load,
  };
}

export function useOrgMe() {
  return useQuery(orgMeQuery());
}

// ============================================
// Contacts
// ============================================

export function useContacts(params: ListParams) {
  return useQuery({
    queryKey: orgKeys.contacts(params),
    placeholderData: keepPreviousData,
    queryFn: () => fetchList<Contact>('/contacts', params),
  });
}

export function useCreateContact(
  options?: UseMutationOptions<Contact, unknown, CreateContactRequest>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateContactRequest) => {
      const res = await foundationApi.post<ObjectEnvelope<Contact>>(`${BASE}/contacts`, body);
      return res.data.result;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: orgKeys.contactsAll });
      options?.onSuccess?.(...args);
    },
  });
}

export function useUpdateContact(
  contactId: number,
  options?: UseMutationOptions<Contact, unknown, UpdateContactRequest>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateContactRequest) => {
      const res = await foundationApi.put<ObjectEnvelope<Contact>>(
        `${BASE}/contacts/${contactId}`,
        body,
      );
      return res.data.result;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: orgKeys.contactsAll });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteContact(options?: UseMutationOptions<void, unknown, number>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (contactId: number) => {
      await foundationApi.delete(`${BASE}/contacts/${contactId}`);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: orgKeys.contactsAll });
      options?.onSuccess?.(...args);
    },
  });
}

export function useSetPrimaryContact(options?: UseMutationOptions<void, unknown, number>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (contactId: number) => {
      await foundationApi.post(`${BASE}/contacts/${contactId}/set-primary`);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: orgKeys.contactsAll });
      qc.invalidateQueries({ queryKey: orgKeys.me });
      options?.onSuccess?.(...args);
    },
  });
}

export function useProvisionLogin(options?: UseMutationOptions<void, unknown, number>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (contactId: number) => {
      await foundationApi.post(`${BASE}/contacts/${contactId}/provision-login`);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: orgKeys.contactsAll });
      options?.onSuccess?.(...args);
    },
  });
}

// ============================================
// Children (sub-customers) — reseller only
// ============================================

export function useChildren(params: ListParams) {
  return useQuery({
    queryKey: orgKeys.children(params),
    placeholderData: keepPreviousData,
    queryFn: () => fetchList<ChildCustomer>('/children', params),
  });
}

export function useChildServices(params: ListParams) {
  return useQuery({
    queryKey: orgKeys.childServices(params),
    placeholderData: keepPreviousData,
    queryFn: () => fetchList<ChildService>('/children/services', params),
  });
}

export function useChildInvoices(params: ListParams) {
  return useQuery({
    queryKey: orgKeys.childInvoices(params),
    placeholderData: keepPreviousData,
    queryFn: () => fetchList<ChildInvoice>('/children/invoices', params),
  });
}

export function useOrgStats(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: orgKeys.stats,
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
    queryFn: () => fetchObject<OrgStats>('/stats'),
  });
}

export function useAddChild(options?: UseMutationOptions<ChildCustomer, unknown, AddChildRequest>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AddChildRequest) => {
      const res = await foundationApi.post<ObjectEnvelope<ChildCustomer>>(`${BASE}/children`, body);
      return res.data.result;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: orgKeys.childrenAll });
      qc.invalidateQueries({ queryKey: orgKeys.stats });
      options?.onSuccess?.(...args);
    },
  });
}

export function useRemoveChild(options?: UseMutationOptions<void, unknown, number>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (customerId: number) => {
      await foundationApi.delete(`${BASE}/children/${customerId}`);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: orgKeys.childrenAll });
      qc.invalidateQueries({ queryKey: orgKeys.stats });
      options?.onSuccess?.(...args);
    },
  });
}

// ============================================
// Admins — reseller only
// ============================================

export function useOrgAdmins(options?: { enabled?: boolean }) {
  // DataCentral answers 403 `not-a-reseller` for this roster on any other org, so
  // never request it until /me says the caller's org is a reseller.
  const { data: me } = useOrgMe();
  return useQuery({
    queryKey: orgKeys.admins,
    enabled: (options?.enabled ?? true) && !!me?.is_reseller,
    queryFn: async (): Promise<OrgAdmin[]> => {
      const res = await foundationApi.get<ArrayEnvelope<OrgAdmin>>(`${BASE}/admins`);
      return res.data?.results ?? [];
    },
  });
}

export function useAddAdmin(options?: UseMutationOptions<OrgAdmin, unknown, AddAdminRequest>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AddAdminRequest) => {
      const res = await foundationApi.post<ObjectEnvelope<OrgAdmin>>(`${BASE}/admins`, body);
      return res.data.result;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: orgKeys.admins });
      options?.onSuccess?.(...args);
    },
  });
}

export function useRemoveAdmin(options?: UseMutationOptions<void, unknown, string>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userUUID: string) => {
      await foundationApi.delete(`${BASE}/admins/${userUUID}`);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: orgKeys.admins });
      options?.onSuccess?.(...args);
    },
  });
}

// ============================================
// Derived role helper
// ============================================

export type OrgRoleName = 'owner' | 'admin' | 'member' | 'viewer';

export interface OrgRole {
  /** Primary contact — the org owner. */
  isPrimary: boolean;
  /** Primary contact OR an org admin with the `owner` role. Full control. */
  isOwner: boolean;
  /** Can manage people/teams/projects: admin flag, owner, or primary. */
  isAdmin: boolean;
  /** Reseller org — unlocks Sub-customers / Admins / Billing sections. */
  isReseller: boolean;
  /** Convenience alias of {@link isAdmin} — the console's manage gate. */
  canManage: boolean;
  /** Read-only user: none of the management roles. */
  isViewer: boolean;
  /** Coarse role label for badges. */
  role: OrgRoleName;
  isLoading: boolean;
}

/**
 * Central role-visibility helper for the Organizations console. Derives the
 * caller's org authority from {@link useOrgMe} (primary/admin/reseller flags)
 * joined with {@link useOrgAdmins} (owner vs admin distinction). Drives sidebar
 * gating, Toolbar primary actions, row menus and destructive controls so every
 * surface agrees on who may do what — mirrors the backend RequireOrgManage.
 */
export function useOrgRole(): OrgRole {
  const { data: me, isLoading: meLoading } = useOrgMe();
  // The admins roster is manage-scoped (RequireOrgManage) and reseller-only
  // (useOrgAdmins gates on is_reseller) — only fetch it for a primary/admin caller
  // so plain members don't hit a 403. Role still derives from the primary flag
  // when the roster is absent.
  const canQueryAdmins = !!me?.is_admin || !!me?.is_primary;
  const { data: admins = [], isLoading: adminsLoading } = useOrgAdmins({ enabled: canQueryAdmins });

  const selfAdmin = me
    ? admins.find(
        (a) =>
          (me.user_uuid && a.user_uuid === me.user_uuid) || a.contact_id === me.contact_id,
      )
    : undefined;

  const isPrimary = !!me?.is_primary;
  const isOwner = isPrimary || selfAdmin?.role === 'owner';
  const isAdmin = !!me?.is_admin || isOwner || selfAdmin?.role === 'admin';
  const isReseller = !!me?.is_reseller;
  const canManage = isAdmin || isPrimary;
  const isViewer = !canManage;
  const role: OrgRoleName = isOwner ? 'owner' : isAdmin ? 'admin' : 'member';

  return {
    isPrimary,
    isOwner,
    isAdmin,
    isReseller,
    canManage,
    isViewer,
    role,
    isLoading: meLoading || adminsLoading,
  };
}
